/* eslint-disable i18next/no-literal-string */
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    getRouteForbidden,
    getRouteReportsSavingsSummary,
    getRouteReportsSavingsSummaryProduct,
    getRouteReportsSavingsSummaryProperty,
} from 'shared/const/router';
import { formatReportPercent } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';
import { SavingsSummaryProductsSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductsSection';
import { SavingsSummaryPropertiesSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryPropertiesSection';
import { SavingsSummaryTotalSavingsMetric } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryTotalSavingsMetric';

import { useReportsPageSavingsSummaryCategory } from '../model/hooks/useReportsPageSavingsSummaryCategory';

import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';

const ReportsPageSavingsSummaryCategory = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const properties = useSelector(getPropertiesList) ?? [];
    const {
        isSavingsSummaryVisible,
        categoryId,
        categoryName,
        loading,
        summary,
        products,
        totalSaved,
        totalSavingsPercent,
        orderCount,
        productsCount,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
    } = useReportsPageSavingsSummaryCategory();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleProductRowClick = useCallback(
        (sku: string, product: ProductMetricsDto) => {
            navigate(getRouteReportsSavingsSummaryProduct(sku), {
                state: {
                    product,
                    fromMissedSavings: false,
                },
            });
        },
        [navigate],
    );

    const handlePropertyRowClick = useCallback(
        ({
            propertyId,
            propertyName,
        }: {
            propertyId: string;
            propertyName?: string;
        }) => {
            navigate(getRouteReportsSavingsSummaryProperty(propertyId), {
                state: {
                    propertyName,
                },
            });
        },
        [navigate],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSavingsSummaryVisible === undefined) {
        return null;
    }

    if (!isSavingsSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!categoryId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-category"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={categoryName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <SavingsSummaryTotalSavingsMetric
                        summary={summary ?? null}
                        primaryAmount={totalSaved}
                        loading={loading}
                        showPercentage={false}
                    />
                    <ReportMetric
                        title={t('savingsSummary.percentOfTotalSavings')}
                        primary={formatReportPercent(totalSavingsPercent)}
                        primaryColor="metrics.positive"
                        loading={loading}
                        minHeight="200px"
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="200px"
                    />
                    <ReportMetric
                        title={t('Products')}
                        primary={productsCount.toLocaleString()}
                        loading={loading}
                        minHeight="200px"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    <Box
                        sx={{
                            marginBottom: '40px',
                        }}
                    >
                        <SavingsSummaryPropertiesSection
                            byPropertiesData={summary?.byPropertiesData ?? []}
                            properties={properties}
                            loading={loading}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            tableOnly
                            tableColumnIds={[
                                'property',
                                'productSourcing',
                                'units',
                                'orders',
                                'avgSupplierSpend',
                                'totalSpend',
                                'totalSaved',
                                'savingsPercentage',
                            ]}
                            onPropertySelect={(payload) => {
                                const propertyId = payload.item.meta
                                    ?.propertyId as string | undefined;
                                if (typeof propertyId === 'string') {
                                    handlePropertyRowClick({
                                        propertyId,
                                        propertyName: payload.item.label,
                                    });
                                }
                            }}
                            showBottomBorder={false}
                        />
                    </Box>
                    <SavingsSummaryProductsSection
                        products={products}
                        totalSavingsAmount={summary?.savingsAmount}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'avgSupplierPrice',
                            'pricePaid',
                            'totalSaved',
                            'percentSaved',
                        ]}
                        showBottomBorder={false}
                    />
                    <SavingsSummaryProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryCategory;
